import { useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { TextInput } from "@/components/design/TextField";
import { BoxHeader } from "@/components/design/BoxHeader";
import type { AccessRoleCatalogQuery } from "@/graphql/generated/operations";
import { AccessRolePermissions } from "./AccessRolePermissions";
import { roleLabel } from "./access-role-labels";
import { permissionLabel } from "./access-permission-labels";

type Permission = AccessRoleCatalogQuery["accessPermissions"]["permissions"][number];
type Role = AccessRoleCatalogQuery["accessRoles"]["roles"][number];

export function AccessPermissionCatalog({ permissions, roles, onOpenRole, onCreateRole }: {
  permissions: Permission[]; roles: Role[];
  onOpenRole: (id: string) => void; onCreateRole: (ids: string[]) => void;
}) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const query = search.trim().toLocaleLowerCase(intl.locale);
  const visible = permissions.filter(permission =>
    [permission.code, permission.description, permissionLabel(intl, permission.code, permission.description)]
      .some(value => value.toLocaleLowerCase(intl.locale).includes(query)));
  const validSelection = selected.filter(id => permissions.some(permission => permission.id === id));
  return <section id="roles-permission-catalog" className="box min-w-0 bg-surface p-6" aria-label={t("admin.roles.catalog")}>
    <BoxHeader title={t("admin.roles.catalog")} />
    <p className="mb-6 text-smycles-body text-ink">{t("admin.roles.catalogHint")}</p>
    <TextInput id="catalog-search" type="search" value={search} onChange={event => setSearch(event.target.value)}
      aria-label={t("admin.roles.search")} placeholder={t("admin.roles.search")} />
    <AccessRolePermissions permissions={visible} selected={validSelection} expandAll={!!query}
      onToggle={id => setSelected(previous => previous.includes(id)
        ? previous.filter(selectedId => selectedId !== id) : [...previous, id])}
      renderDetails={permission => <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
        {roles.filter(role => role.permissions.some(item => item.id === permission.id)).map(role =>
          <button type="button" key={role.id} className="text-smycles-meta underline underline-offset-2"
            onClick={() => onOpenRole(role.id)}>{roleLabel(intl, role)}</button>)}
      </div>} />
    {!visible.length && <p className="py-4 text-smycles-body text-ink">{t("admin.roles.noResults")}</p>}
    <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-ink/10 pt-4">
      <p role="status" className="text-smycles-meta">{t("admin.roles.selected", { count: validSelection.length })}</p>
      <Button id="catalog-create-role" disabled={!validSelection.length} onClick={() => onCreateRole(validSelection)}>
        {t("admin.roles.createFromSelection")}</Button>
    </div>
  </section>;
}
